import { useEffect, useState } from "react";
import { toast } from "sonner";
import GlassSheet from "@/components/v2/GlassSheet";
import { api } from "@/hooks/useAuth";

export default function SankalpSheet({ open, onClose, user }) {
  const [items, setItems] = useState([]);
  const [statement, setStatement] = useState("");
  const [duration, setDuration] = useState(30);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/sankalp"); setItems(data.items || []); } catch (e) {}
  };
  useEffect(() => { if (open && user) load(); }, [open, user]);

  const propose = async () => {
    if (!statement.trim()) return;
    setCreating(true);
    try {
      await api.post("/sankalp/propose", { statement: statement.trim(), duration_days: duration });
      setStatement(""); await load();
    } catch (e) { toast.error("Please sign in to make a Sankalp."); }
    finally { setCreating(false); }
  };

  const accept = async (id) => { try { await api.post(`/sankalp/${id}/accept`); await load(); toast.success("Sankalp accepted."); } catch (e) {} };
  const complete = async (id) => { try { await api.post(`/sankalp/${id}/complete`); await load(); toast.success("🙏 Sankalp fulfilled."); } catch (e) {} };
  const abandon = async (id) => { try { await api.post(`/sankalp/${id}/abandon`); await load(); } catch (e) {} };

  const active = items.filter((i) => ["proposed", "accepted", "active"].includes(i.status));
  const past = items.filter((i) => ["completed", "abandoned", "paused"].includes(i.status));

  return (
    <GlassSheet open={open} onClose={onClose} eyebrow="My Sankalp" title="A commitment you make with KALKI." testid="sankalp-sheet">
      <p className="text-slate-300 text-[13.5px] leading-relaxed mb-5">
        A Sankalp is one meaningful promise to walk a path with full attention — not a to-do list. Accept it when you are ready.
      </p>

      <div className="space-y-3 mb-6">
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">What do you want to commit to?</span>
          <textarea
            value={statement} onChange={(e) => setStatement(e.target.value)} rows={2}
            data-testid="sankalp-statement"
            placeholder="For the next 30 days, I will…"
            className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-slate-100 focus:border-amber-300/50 focus:outline-none"
          />
        </label>
        <div className="flex items-center gap-3">
          <label className="flex-1">
            <span className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">For how many days?</span>
            <select data-testid="sankalp-duration" value={duration} onChange={(e) => setDuration(parseInt(e.target.value, 10))}
                    className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-slate-100">
              <option value={7}>7 days</option>
              <option value={21}>21 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
          </label>
          <button onClick={propose} disabled={creating || !statement.trim()} data-testid="sankalp-propose"
            className="rounded-full px-5 py-3 text-[13.5px] font-medium text-slate-50 disabled:opacity-40 self-end"
            style={{ background: "linear-gradient(140deg,#3B82F6,#7C3AED)", border: "1px solid rgba(223,185,110,0.5)" }}>
            Propose
          </button>
        </div>
      </div>

      {active.length > 0 && (
        <div className="space-y-3 mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/90">Active</div>
          {active.map((s) => (
            <div key={s.id} className="rounded-2xl border border-amber-300/30 bg-amber-100/5 px-4 py-3">
              <div className="text-slate-100 text-[14px] font-medium mb-1">{s.statement}</div>
              <div className="text-[11px] text-slate-400 tracking-wide">
                {s.duration_days} days · <span className="uppercase">{s.status}</span>
              </div>
              <div className="mt-3 flex gap-2">
                {s.status === "proposed" && (
                  <button data-testid={`sankalp-accept-${s.id}`} onClick={() => accept(s.id)}
                    className="text-[12px] rounded-full px-4 py-1.5 bg-white/10 text-slate-100 hover:bg-white/15">Accept</button>
                )}
                {s.status !== "proposed" && (
                  <button data-testid={`sankalp-complete-${s.id}`} onClick={() => complete(s.id)}
                    className="text-[12px] rounded-full px-4 py-1.5 border border-amber-300/40 text-amber-200 hover:bg-amber-100/10">Mark complete</button>
                )}
                <button onClick={() => abandon(s.id)} className="text-[11px] text-slate-500 hover:text-slate-300 px-2">Let go</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {past.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400">Past</div>
          {past.map((s) => (
            <div key={s.id} className="rounded-xl border border-white/5 bg-white/5 px-4 py-2 flex items-center justify-between">
              <span className="text-slate-300 text-[13px]">{s.statement}</span>
              <span className="text-[10px] uppercase text-slate-500 tracking-wider">{s.status}</span>
            </div>
          ))}
        </div>
      )}
    </GlassSheet>
  );
}
